import React, { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore, useCompanyStore } from '@/store';
import { useSidebarContext } from './SidebarContext';
import { useI18n } from '@/components/I18nProvider';
import { cn } from '@/lib/utils';
import { B2BonixLogo } from '@/components/B2BonixLogo';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  CheckCircle,
  MessageCircle,
  UserCog,
  Settings,
  LogOut,
  Building2,
  Shield,
  X,
  Package,
  ShoppingCart,
  Store,
  Wallet,
  Plug,
  Image,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

const PERMISSION_MAP = {
  '/admin': 'dashboard',
  '/admin/dealers': 'dealers',
  '/admin/products': 'products',
  '/admin/orders': 'orders',
  '/admin/payment-tracking': 'payment_tracking',
  '/admin/whatsapp': 'whatsapp',
  '/admin/integrations': 'integrations',
  '/admin/security': 'security',
  '/admin/settings': 'settings',
};

const adminNavItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Genel Bakış' },
  { to: '/admin/dealers', icon: Store, label: 'Bayiler' },
  { to: '/admin/products', icon: Package, label: 'Ürünler' },
  { to: '/admin/shop-settings', icon: Image, label: 'Mağaza Yönetimi' },
  { to: '/admin/orders', icon: ShoppingCart, label: 'Siparişler' },
  { to: '/admin/payment-tracking', icon: Wallet, label: 'Ödeme Takibi' },
  { to: '/admin/whatsapp', icon: MessageCircle, label: 'WhatsApp' },
  { to: '/admin/personnel', icon: UserCog, label: 'Personel' },
  { to: '/admin/integrations', icon: Plug, label: 'Entegrasyonlar' },
  { to: '/admin/security', icon: Shield, label: 'Güvenlik Logları' },
  { to: '/admin/settings', icon: Settings, label: 'Sistem Ayarları' },
];

export const Sidebar = () => {
  const { user, logout, isTrialExpired } = useAuthStore();
  const { company, fetchCompany } = useCompanyStore();
  const { sidebarOpen, setSidebarOpen } = useSidebarContext();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.company_id && user?.role !== 'super_admin') {
      fetchCompany();
    }
  }, [user?.company_id, user?.role, fetchCompany]);

  // Apply company color
  useEffect(() => {
    if (company?.primary_color) {
      const hex = company.primary_color;
      let r = parseInt(hex.slice(1, 3), 16) / 255;
      let g = parseInt(hex.slice(3, 5), 16) / 255;
      let b = parseInt(hex.slice(5, 7), 16) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;
      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
          default: break;
        }
      }
      const hsl = `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
      document.documentElement.style.setProperty('--primary', hsl);
    }
  }, [company?.primary_color]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  const getInitials = (name) => {
    return name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const getRoleLabel = (role) => {
    const labels = { super_admin: 'Super Admin', company_admin: 'Firma Yöneticisi', personnel: 'Personel' };
    return labels[role] || role;
  };

  const allCompanyNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, labelKey: 'dashboard' },
    { to: '/customers', icon: Users, labelKey: 'customers' },
    { to: '/payments', icon: CreditCard, labelKey: 'payments' },
    { to: '/collected', icon: CheckCircle, labelKey: 'todayCollected' },
    { to: '/whatsapp', icon: MessageCircle, labelKey: 'whatsapp' },
    { to: '/personnel', icon: UserCog, labelKey: 'personnel', adminOnly: true },
    { to: '/settings', icon: Settings, labelKey: 'settings', adminOnly: true },
  ];

  // Determine nav items based on role and subscription status
  let navItems;
  if (user?.role === 'super_admin') {
    navItems = adminNavItems;
  } else if (user?.role === 'personnel') {
    const perms = user?.permissions || [];
    navItems = adminNavItems.filter(item => {
      // Personnel page only visible to super_admin
      if (item.to === '/admin/personnel') return false;
      const requiredPerm = PERMISSION_MAP[item.to];
      if (!requiredPerm) return false;
      return perms.includes(requiredPerm);
    });
  } else if (isTrialExpired && user?.role === 'company_admin') {
    navItems = [{ to: '/settings', icon: Settings, labelKey: 'settings' }];
  } else {
    navItems = allCompanyNavItems;
  }
  const isAdmin = user?.role === 'super_admin' || user?.role === 'personnel';

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen w-64 flex-col transition-transform duration-300 lg:translate-x-0',
          'bg-gradient-to-b from-[#222a63] via-[#1a224f] to-[#12173a] text-white',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-white/10 px-4">
          <div className="flex items-center gap-2">
            <B2BonixLogo variant="light" iconSize={34} asLink={false} showSubtitle />
          </div>
          <Button variant="ghost" size="icon" className="lg:hidden text-white hover:bg-white/10" onClick={() => setSidebarOpen(false)} data-testid="close-sidebar-btn">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-4" data-testid="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              onClick={handleNavClick}
              data-testid={`nav-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200',
                  isActive ? 'bg-white/20 text-yellow-300' : 'text-white/60 hover:bg-white/10 hover:text-white'
                )
              }
            >
              <item.icon className="h-5 w-5" />
              <span className="notranslate" translate="no">{item.label || t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Section */}
        <div className="border-t border-white/10 p-4">
          <div className="mb-3 flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-white/20 text-yellow-300">{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col overflow-hidden">
              <span className="truncate text-sm font-medium text-white">{user?.name}</span>
              <span className="truncate text-xs text-white/50">{getRoleLabel(user?.role)}</span>
            </div>
          </div>
          <Button variant="outline" className="w-full justify-start gap-2 border-white/20 text-white/70 hover:bg-white/10 hover:text-white" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" />
            {t('logout')}
          </Button>
        </div>
      </aside>
    </>
  );
};
